import { DataProfileDatasource } from '@/services/endpoints/marketIntelligence';

export type CoverageLevel = 'all' | 'partial' | 'none';

export type DistributionItem = {
  label: string;
  percent: number;
};

export type CoverageRow = {
  id: string;
  traitKeys: string[];
  traitCount: number;
  coverage: CoverageLevel;
};

export const SEMANTIC_TRAIT_GROUPS: Record<string, string[]> = {
  email: ['email', 'email_address'],
  name: ['name', 'first_name', 'last_name', 'full_name', 'username'],
  birthday: ['birthday', 'birthdate', 'date_of_birth', 'birth_date'],
  address: [
    'address',
    'street',
    'city',
    'country',
    'address_city',
    'address_country',
    'country_code',
    'billing_country',
    'shipping_country',
    'billing_city',
    'shipping_city',
  ],
};

const COVERAGE_THRESHOLD = 0.7; // Maps to coverage.all badge ("Most contacts") in dataProfile UI
const MAX_DISTRIBUTION_ITEMS = 6;
const DEFAULT_COVERAGE_ROW_LIMIT = 8;

export const normalizeTraitKey = (value: string) => value.trim().toLowerCase();

export const formatTraitLabel = (traitKey: string): string =>
  traitKey
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const discoverTopValueTraits = (datasources: DataProfileDatasource[]): string[] => {
  const traitTotals = new Map<string, number>();

  datasources.forEach((datasource) => {
    Object.entries(datasource.stats.topValues || {}).forEach(([trait, group]) => {
      traitTotals.set(trait, (traitTotals.get(trait) || 0) + group.totalOccurrences);
    });
  });

  return Array.from(traitTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([trait]) => trait);
};

export const discoverCoverageTraits = (datasources: DataProfileDatasource[]): string[] => {
  const traits = new Set<string>();

  datasources.forEach((datasource) => {
    Object.keys(datasource.stats.distinctValuesByTrait || {}).forEach((trait) => traits.add(trait));
  });

  return Array.from(traits).sort((a, b) => a.localeCompare(b));
};

export const getWorkspacePopulation = (datasources: DataProfileDatasource[]): number => {
  const maxEvents = datasources.reduce((max, datasource) => Math.max(max, datasource.stats.totalEvents || 0), 0);

  if (maxEvents > 0) {
    return maxEvents;
  }

  return datasources.reduce((maxCount, datasource) => {
    const values = Object.values(datasource.stats.distinctValuesByTrait || {});
    const datasourceMax = values.length ? Math.max(...values) : 0;

    return Math.max(maxCount, datasourceMax);
  }, 0);
};

export const aggregateTopValues = (datasources: DataProfileDatasource[], traitKey: string): DistributionItem[] => {
  const normalizedKey = normalizeTraitKey(traitKey);
  const totals = new Map<string, number>();

  datasources.forEach((datasource) => {
    const group = Object.entries(datasource.stats.topValues || {}).find(([trait]) => normalizeTraitKey(trait) === normalizedKey)?.[1];

    group?.values.forEach((item) => {
      totals.set(item.value, (totals.get(item.value) || 0) + item.occurrences);
    });
  });

  const totalOccurrences = Array.from(totals.values()).reduce((sum, value) => sum + value, 0);

  return Array.from(totals.entries())
    .map(([label, occurrences]) => ({
      label,
      percent: totalOccurrences ? Math.round((occurrences / totalOccurrences) * 100) : 0,
    }))
    .sort((a, b) => b.percent - a.percent)
    .slice(0, MAX_DISTRIBUTION_ITEMS);
};

export const getDistributionCoveragePct = (datasources: DataProfileDatasource[], traitKey: string): number | null => {
  const normalizedKey = normalizeTraitKey(traitKey);
  let weightedCoverage = 0;
  let totalWeight = 0;

  datasources.forEach((datasource) => {
    const group = Object.entries(datasource.stats.topValues || {}).find(([trait]) => normalizeTraitKey(trait) === normalizedKey)?.[1];

    if (!group) {
      return;
    }

    weightedCoverage += group.coveragePct * group.totalOccurrences;
    totalWeight += group.totalOccurrences;
  });

  if (!totalWeight) {
    return null;
  }

  return Math.round(weightedCoverage / totalWeight);
};

export const groupTraitsBySemantics = (traits: string[]): Array<{ id: string; traitKeys: string[] }> => {
  const assigned = new Set<string>();
  const groups: Array<{ id: string; traitKeys: string[] }> = [];

  Object.entries(SEMANTIC_TRAIT_GROUPS).forEach(([groupId, aliases]) => {
    const normalizedAliases = aliases.map(normalizeTraitKey);
    const matchingKeys = traits.filter((trait) => normalizedAliases.includes(normalizeTraitKey(trait)));

    if (!matchingKeys.length) {
      return;
    }

    groups.push({ id: groupId, traitKeys: matchingKeys });
    matchingKeys.forEach((trait) => assigned.add(normalizeTraitKey(trait)));
  });

  traits.forEach((trait) => {
    if (assigned.has(normalizeTraitKey(trait))) {
      return;
    }

    groups.push({ id: trait, traitKeys: [trait] });
  });

  return groups;
};

export const getGroupedTraitCount = (datasources: DataProfileDatasource[], traitKeys: string[]): number => {
  const normalizedKeys = traitKeys.map(normalizeTraitKey);

  return datasources.reduce((total, datasource) => {
    const matches = Object.entries(datasource.stats.distinctValuesByTrait || {})
      .filter(([trait]) => normalizedKeys.includes(normalizeTraitKey(trait)))
      .map(([, count]) => count);
    const datasourceMax = matches.length ? Math.max(...matches) : 0;

    return total + datasourceMax;
  }, 0);
};

export const getTraitCoverageLevel = (population: number, traitCount: number): CoverageLevel => {
  if (!traitCount || !population) {
    return 'none';
  }

  return traitCount >= population * COVERAGE_THRESHOLD ? 'all' : 'partial';
};

export const buildCoverageRows = (datasources: DataProfileDatasource[]): CoverageRow[] => {
  const traits = discoverCoverageTraits(datasources);
  const population = getWorkspacePopulation(datasources);
  const groups = groupTraitsBySemantics(traits);

  return groups
    .map((group) => {
      const traitCount = getGroupedTraitCount(datasources, group.traitKeys);

      return {
        id: group.id,
        traitKeys: group.traitKeys,
        traitCount,
        coverage: getTraitCoverageLevel(population, traitCount),
      };
    })
    .sort((a, b) => b.traitCount - a.traitCount);
};

export const getVisibleCoverageRows = (rows: CoverageRow[], showAll: boolean, limit = DEFAULT_COVERAGE_ROW_LIMIT) =>
  showAll ? rows : rows.slice(0, limit);

export const getDefaultDistributionTrait = (datasources: DataProfileDatasource[]): string | null => {
  const traits = discoverTopValueTraits(datasources);

  return traits[0] ?? null;
};
